import React, { useState } from 'react';

import { useAppDispatch, useAppSelector } from '../hooks';

import { MandateActions } from '../actions/mandate/mandate.actions';

import type { Mandate } from '../interfaces/client/mandate.interface';
import type { Employee } from '../interfaces/employee/employee.interface';

import Modal from './components/Modal';
import Icon from './components/Icon';
import { num, formatZar } from './lib/mandate-helpers';

// =============================================================================
//  Mandate edit modal (v3 — full)
//
//  Replaces the legacy /clients/:id/mandates/:id edit form for mandate-side
//  edits.  Three sections:
//
//    Workflow  — stage, progress, lead_id
//    Project   — name, transaction_type
//    Money     — transaction_size, transaction_fee, fee (retainer)
//    Dates     — signed, start_date, expected_end_date
//
//  Save dispatches MandateActions.updateMandate which PUTs to /mandates/:id
//  and refreshes the global mandate store on success.
// =============================================================================

const STAGE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'execution', label: 'Execution' },
  { value: 'due_diligence', label: 'Due Diligence' },
  { value: 'pre_listing', label: 'Pre-listing' },
  { value: 'implementation', label: 'Implementation' },
  { value: 'closing', label: 'Closing' },
  { value: 'reporting', label: 'Reporting' },
  { value: 'structuring', label: 'Structuring' },
  { value: 'field_work', label: 'Field Work' },
  { value: 'closed', label: 'Closed' }
];

// Common deal type suggestions for the datalist.  Free-text so users can
// type anything not in the list — the column is `transaction_type:string`.
const TYPE_SUGGESTIONS = [
  'Capital Raise',
  'M&A — Sell-side',
  'M&A — Buy-side',
  'JSE Listing',
  'Restructuring',
  'Disposal',
  'Valuation',
  'BEE Advisory',
  'Due Diligence',
  'Independent Business Review',
  'Issuer Agent Role - CTSE',
  'Information Sourcing'
];

interface Props {
  mandate: Mandate;
  onClose: () => void;
}

/** Convert a date-ish value into the `<input type="date">` YYYY-MM-DD format. */
const isoDateInput = (input: Mandate['signed'] | undefined): string => {
  if (!input) return '';
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
};

const MandateEditModal = ({ mandate, onClose }: Props) => {
  const dispatch = useAppDispatch();
  const employees: Employee[] =
    useAppSelector(({ employee_store }) => employee_store.employees) || [];

  // Workflow ---------------------------------------------------------------
  const [stage, setStage] = useState<string>(mandate.stage || 'execution');
  const [progress, setProgress] = useState<number>(
    typeof mandate.progress === 'number' ? mandate.progress : 0
  );
  const [leadId, setLeadId] = useState<string>(
    mandate.lead_id !== undefined && mandate.lead_id !== null ? String(mandate.lead_id) : ''
  );

  // Project ---------------------------------------------------------------
  const [name, setName] = useState<string>(mandate.name || '');
  const [transactionType, setTransactionType] = useState<string>(mandate.transaction_type || '');

  // Money -----------------------------------------------------------------
  const [transactionSize, setTransactionSize] = useState<string>(
    String(num(mandate.transaction_size) || '')
  );
  const [transactionFee, setTransactionFee] = useState<string>(
    String(num(mandate.transaction_fee) || '')
  );
  const [retainer, setRetainer] = useState<string>(String(num(mandate.fee) || ''));

  // Dates -----------------------------------------------------------------
  const [signed, setSigned] = useState<string>(isoDateInput(mandate.signed));
  const [startDate, setStartDate] = useState<string>(isoDateInput(mandate.start_date));
  const [expectedEnd, setExpectedEnd] = useState<string>(isoDateInput(mandate.expected_end_date));

  const [saving, setSaving] = useState(false);

  const handleSave = () => {
    setSaving(true);
    dispatch(
      MandateActions.updateMandate(
        mandate.id as number | string,
        {
          // Workflow
          stage,
          progress,
          lead_id: leadId === '' ? undefined : Number(leadId),
          // Project
          name,
          transaction_type: transactionType,
          // Money — empty string → undefined so Rails keeps existing values
          transaction_size: transactionSize === '' ? 0 : Number(transactionSize),
          transaction_fee: transactionFee === '' ? 0 : Number(transactionFee),
          fee: retainer === '' ? 0 : Number(retainer),
          // Dates (omit when blank to avoid setting to null)
          ...(signed ? { signed } : {}),
          ...(startDate ? { start_date: startDate } : {}),
          ...(expectedEnd ? { expected_end_date: expectedEnd } : {})
        } as Partial<Mandate> as Mandate,
        () => {
          setSaving(false);
          onClose();
        }
      )
    );
  };

  const footer = (
    <>
      <button type="button" className="btn ghost" onClick={onClose} disabled={saving}>
        Cancel
      </button>
      <button type="button" className="btn primary" onClick={handleSave} disabled={saving}>
        {saving ? 'Saving…' : 'Save changes'}
      </button>
    </>
  );

  return (
    <Modal title={`Edit ${mandate.name}`} onClose={onClose} footer={footer} wide>
      {/* --- Workflow ------------------------------------------------- */}
      <section className="v3-modal-section">
        <h4 className="section-title">Workflow</h4>

        <div className="field">
          <span className="label">Stage</span>
          <select value={stage} onChange={(e) => setStage(e.target.value)}>
            {STAGE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <span className="label">Progress</span>
          <div className="progress-edit">
            <input
              type="range"
              min={0}
              max={100}
              value={progress}
              onChange={(e) => setProgress(Number(e.target.value))}
            />
            <input
              type="number"
              className="num-input"
              min={0}
              max={100}
              value={progress}
              onChange={(e) => {
                const n = Number(e.target.value);
                if (Number.isFinite(n)) setProgress(Math.max(0, Math.min(100, n)));
              }}
            />
            <span className="pct">{progress}%</span>
          </div>
        </div>

        <div className="field">
          <span className="label">Lead partner</span>
          <select value={leadId} onChange={(e) => setLeadId(e.target.value)}>
            <option value="">— Unassigned —</option>
            {employees.map((e) => (
              <option key={e.id} value={String(e.id)}>
                {e.first_name} {e.last_name}
                {e.erole ? ` · ${e.erole}` : ''}
              </option>
            ))}
          </select>
          {employees.length === 0 && (
            <span className="label" style={{ textTransform: 'none', letterSpacing: 0 }}>
              <Icon name="clock" size={12} /> Employee list still loading…
            </span>
          )}
        </div>
      </section>

      {/* --- Project -------------------------------------------------- */}
      <section className="v3-modal-section">
        <h4 className="section-title">Project</h4>

        <div className="field">
          <span className="label">Project name</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Zestcor Eleven Proprietary Limited"
          />
        </div>

        <div className="field">
          <span className="label">Transaction type</span>
          <input
            type="text"
            list="v3-mandate-types"
            value={transactionType}
            onChange={(e) => setTransactionType(e.target.value)}
            placeholder="e.g. Capital Raise"
          />
          <datalist id="v3-mandate-types">
            {TYPE_SUGGESTIONS.map((t) => (
              <option key={t} value={t} />
            ))}
          </datalist>
        </div>
      </section>

      {/* --- Money --------------------------------------------------- */}
      <section className="v3-modal-section">
        <h4 className="section-title">Money</h4>

        <div className="field">
          <span className="label">Transaction size (ZAR)</span>
          <input
            type="number"
            min={0}
            step={1000}
            value={transactionSize}
            onChange={(e) => setTransactionSize(e.target.value)}
            placeholder="e.g. 1800000000"
          />
          <span
            className="muted"
            style={{ fontSize: 11.5, marginTop: 2, textTransform: 'none', letterSpacing: 0 }}
          >
            Displays as {formatZar(transactionSize === '' ? 0 : Number(transactionSize))}.
          </span>
        </div>

        <div className="field row-2">
          <div className="field" style={{ gap: 6 }}>
            <span className="label">Transaction fee (ZAR)</span>
            <input
              type="number"
              min={0}
              step={1000}
              value={transactionFee}
              onChange={(e) => setTransactionFee(e.target.value)}
            />
          </div>
          <div className="field" style={{ gap: 6 }}>
            <span className="label">Retainer (ZAR)</span>
            <input
              type="number"
              min={0}
              step={1000}
              value={retainer}
              onChange={(e) => setRetainer(e.target.value)}
            />
          </div>
        </div>
      </section>

      {/* --- Dates --------------------------------------------------- */}
      <section className="v3-modal-section">
        <h4 className="section-title">Dates</h4>

        <div className="field row-2">
          <div className="field" style={{ gap: 6 }}>
            <span className="label">Signed</span>
            <input type="date" value={signed} onChange={(e) => setSigned(e.target.value)} />
          </div>
          <div className="field" style={{ gap: 6 }}>
            <span className="label">Start</span>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
        </div>

        <div className="field">
          <span className="label">Target close</span>
          <input type="date" value={expectedEnd} onChange={(e) => setExpectedEnd(e.target.value)} />
        </div>
      </section>
    </Modal>
  );
};

export default MandateEditModal;
